import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import type {
  APIResponse,
  MatterPhaseTeamInput,
  MatterPhaseAssignmentInput,
  MatterPhaseApplication,
  MatterPhaseApplicationOption,
  MatterPhaseActionRetry,
  MatterPhasePagination,
  MatterPhaseProjectLifecycle,
  PhaseActionDefinition,
  PhaseProject,
  PhaseProjectInput,
  PhaseProjectRetroactiveApplication,
  ReusableMatterPhase,
  ReusableMatterPhaseLifecycle,
} from "@/lib/types";

const matterPhaseConfigurationRoute = "matters/v2";
const matterPhaseRuntimeRoute = "matters";

export type PaginatedMatterPhaseResponse<T> = APIResponse<T> & {
  pagination: MatterPhasePagination;
};

export interface MatterPhaseListParams {
  q?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface PhaseProjectListParams extends MatterPhaseListParams {
  lifecycle?: MatterPhaseProjectLifecycle;
  incidentTypeId?: string;
}

export interface ReusablePhaseListParams extends MatterPhaseListParams {
  lifecycle?: ReusableMatterPhaseLifecycle;
}

export const retrievePhaseProjects = async (
  params: PhaseProjectListParams = {},
) =>
  authenticatedFastApiAxiosInstance
    .get<
      PaginatedMatterPhaseResponse<PhaseProject[]>
    >(`${matterPhaseConfigurationRoute}/phase-projects`, { params })
    .then((response) => response.data);

export const retrievePhaseProject = async (projectId: string) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<PhaseProject>
    >(`${matterPhaseConfigurationRoute}/phase-projects/${projectId}`)
    .then((response) => response.data);

export const createPhaseProject = async (data: PhaseProjectInput) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<PhaseProject>
    >(`${matterPhaseConfigurationRoute}/phase-projects`, data)
    .then((response) => response.data);

export const updatePhaseProject = async ({
  projectId,
  dateModified,
  ...data
}: PhaseProjectInput & { projectId: string; dateModified: string }) =>
  authenticatedFastApiAxiosInstance
    .put<
      APIResponse<PhaseProject>
    >(`${matterPhaseConfigurationRoute}/phase-projects/${projectId}`, { ...data, dateModified })
    .then((response) => response.data);

export const retroactivelyApplyPhaseProject = async ({
  projectId,
  expectedPhaseProjectRevisionId,
}: {
  projectId: string;
  expectedPhaseProjectRevisionId: string;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<PhaseProjectRetroactiveApplication>
    >(`${matterPhaseConfigurationRoute}/phase-projects/${projectId}/retroactive-applications`, { expectedPhaseProjectRevisionId })
    .then((response) => response.data);

export const activatePhaseProject = async ({
  projectId,
  dateModified,
}: {
  projectId: string;
  dateModified: string;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<PhaseProject>
    >(`${matterPhaseConfigurationRoute}/phase-projects/${projectId}/activate`, { dateModified })
    .then((response) => response.data);

export const archivePhaseProject = async ({
  projectId,
  dateModified,
}: {
  projectId: string;
  dateModified: string;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<PhaseProject>
    >(`${matterPhaseConfigurationRoute}/phase-projects/${projectId}/archive`, { dateModified })
    .then((response) => response.data);

export const reactivatePhaseProject = async ({
  projectId,
  dateModified,
}: {
  projectId: string;
  dateModified: string;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<PhaseProject>
    >(`${matterPhaseConfigurationRoute}/phase-projects/${projectId}/reactivate`, { dateModified })
    .then((response) => response.data);

export const deletePhaseProject = async (projectId: string) =>
  authenticatedFastApiAxiosInstance
    .delete<
      APIResponse<PhaseProject>
    >(`${matterPhaseConfigurationRoute}/phase-projects/${projectId}`)
    .then((response) => response.data);

export const retrieveReusablePhases = async (
  params: ReusablePhaseListParams = {},
) =>
  authenticatedFastApiAxiosInstance
    .get<
      PaginatedMatterPhaseResponse<ReusableMatterPhase[]>
    >(`${matterPhaseConfigurationRoute}/reusable-phases`, { params })
    .then((response) => response.data);

export const createReusablePhase = async (data: {
  title: string;
  description: string | null;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<ReusableMatterPhase>
    >(`${matterPhaseConfigurationRoute}/reusable-phases`, data)
    .then((response) => response.data);

export const updateReusablePhase = async ({
  reusablePhaseId,
  ...data
}: {
  reusablePhaseId: string;
  dateModified: string;
  title: string;
  description: string | null;
}) =>
  authenticatedFastApiAxiosInstance
    .put<
      APIResponse<ReusableMatterPhase>
    >(`${matterPhaseConfigurationRoute}/reusable-phases/${reusablePhaseId}`, data)
    .then((response) => response.data);

export const archiveReusablePhase = async ({
  reusablePhaseId,
  dateModified,
}: {
  reusablePhaseId: string;
  dateModified: string;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<ReusableMatterPhase>
    >(`${matterPhaseConfigurationRoute}/reusable-phases/${reusablePhaseId}/archive`, { dateModified })
    .then((response) => response.data);

export const deleteReusablePhase = async ({
  reusablePhaseId,
  dateModified,
}: {
  reusablePhaseId: string;
  dateModified: string;
}) =>
  authenticatedFastApiAxiosInstance
    .delete<
      APIResponse<ReusableMatterPhase>
    >(`${matterPhaseConfigurationRoute}/reusable-phases/${reusablePhaseId}`, { data: { dateModified } })
    .then((response) => response.data);

export const retrievePhaseActionDefinitions = async (
  params: MatterPhaseListParams = {},
) =>
  authenticatedFastApiAxiosInstance
    .get<
      PaginatedMatterPhaseResponse<PhaseActionDefinition[]>
    >(`${matterPhaseConfigurationRoute}/phase-action-definitions`, { params })
    .then((response) => response.data);

export const createManualPhaseAction = async (data: {
  title: string;
  description: string | null;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<PhaseActionDefinition>
    >(`${matterPhaseConfigurationRoute}/phase-action-definitions`, data)
    .then((response) => response.data);

export const retrieveAllReusablePhases = async (pageSize = 500) => {
  const firstPage = await retrieveReusablePhases({ pageNumber: 1, pageSize });
  const data = [...firstPage.data];
  const pageCount = Math.ceil(firstPage.pagination.count / pageSize);
  for (let pageNumber = 2; pageNumber <= pageCount; pageNumber += 1) {
    const page = await retrieveReusablePhases({ pageNumber, pageSize });
    data.push(...page.data);
  }
  return { ...firstPage, data };
};

export const retrieveAllPhaseActionDefinitions = async (pageSize = 500) => {
  const firstPage = await retrievePhaseActionDefinitions({
    pageNumber: 1,
    pageSize,
  });
  const data = [...firstPage.data];
  const pageCount = Math.ceil(firstPage.pagination.count / pageSize);
  for (let pageNumber = 2; pageNumber <= pageCount; pageNumber += 1) {
    const page = await retrievePhaseActionDefinitions({
      pageNumber,
      pageSize,
    });
    data.push(...page.data);
  }
  return { ...firstPage, data };
};

export const retrieveMatterPhases = async (matterId: string) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<MatterPhaseApplication | null>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phases`)
    .then((response) => response.data);

export const retrieveMatterPhaseApplicationOption = async (matterId: string) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<MatterPhaseApplicationOption[]>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phase-project-application-option`)
    .then((response) => response.data);

export const applyMatterPhaseProject = async ({
  matterId,
  projectId,
  expectedPhaseProjectRevisionId,
  idempotencyKey,
}: {
  matterId: string;
  projectId: string;
  expectedPhaseProjectRevisionId: string;
  idempotencyKey: string;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterPhaseApplication>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phase-project-applications`, { projectId, expectedPhaseProjectRevisionId }, { headers: { "Idempotency-Key": idempotencyKey } })
    .then((response) => response.data);

export const removeMatterPhaseProject = async ({
  matterId,
  applicationId,
  expectedGenerationId,
}: {
  matterId: string;
  applicationId: string;
  expectedGenerationId: string;
}) =>
  authenticatedFastApiAxiosInstance
    .delete<
      APIResponse<MatterPhaseApplication | null>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phase-project-applications/${applicationId}`, { data: { expectedGenerationId } })
    .then((response) => response.data);

type MatterPhaseManualStatusInput = {
  matterId: string;
  status: "Blocked" | "Closed";
  reason: string;
  expectedStateVersion: number;
};

type MatterPhaseManualStatusClearInput = {
  matterId: string;
  expectedStateVersion: number;
  note?: string;
};

export const setMatterPhaseManualStatus = async ({
  matterId,
  phaseId,
  ...data
}: MatterPhaseManualStatusInput & { phaseId: string }) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterPhaseApplication>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phases/${phaseId}/manual-status`, data)
    .then((response) => response.data);

export const clearMatterPhaseManualStatus = async ({
  matterId,
  phaseId,
  ...data
}: MatterPhaseManualStatusClearInput & { phaseId: string }) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterPhaseApplication>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phases/${phaseId}/manual-status/clear`, data)
    .then((response) => response.data);

export const setMatterPhaseActionManualStatus = async ({
  matterId,
  actionId,
  ...data
}: MatterPhaseManualStatusInput & { actionId: string }) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterPhaseApplication>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phase-actions/${actionId}/manual-status`, data)
    .then((response) => response.data);

export const clearMatterPhaseActionManualStatus = async ({
  matterId,
  actionId,
  ...data
}: MatterPhaseManualStatusClearInput & { actionId: string }) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterPhaseApplication>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phase-actions/${actionId}/manual-status/clear`, data)
    .then((response) => response.data);

export const retryMatterPhaseAction = async ({
  matterId,
  actionId,
  idempotencyKey,
}: {
  matterId: string;
  actionId: string;
  idempotencyKey: string;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterPhaseActionRetry>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phase-actions/${actionId}/retry`, undefined, { headers: { "Idempotency-Key": idempotencyKey } })
    .then((response) => response.data);

export const setMatterPhaseActionCompletion = async ({
  matterId,
  actionId,
  completed,
  expectedStateVersion,
}: {
  matterId: string;
  actionId: string;
  completed: boolean;
  expectedStateVersion: number;
}) =>
  authenticatedFastApiAxiosInstance
    .put<
      APIResponse<MatterPhaseApplication>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phase-actions/${actionId}/completion`, { completed, expectedStateVersion })
    .then((response) => response.data);

export const setMatterPhaseTeam = async ({
  matterId,
  phaseId,
  ...data
}: MatterPhaseTeamInput & { matterId: string; phaseId: string }) =>
  authenticatedFastApiAxiosInstance
    .put<
      APIResponse<MatterPhaseApplication>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phases/${phaseId}/team`, data)
    .then((response) => response.data);

export const setMatterPhaseAssignment = async ({
  matterId,
  phaseId,
  mode,
  teamId,
  userId,
  roleId,
  expectedGenerationId,
  expectedStateVersion,
}: MatterPhaseAssignmentInput & { matterId: string; phaseId: string }) =>
  authenticatedFastApiAxiosInstance
    .put<
      APIResponse<MatterPhaseApplication | null>
    >(`${matterPhaseRuntimeRoute}/${matterId}/phases/${phaseId}/assignment`, { mode, teamId, userId, roleId, expectedGenerationId, expectedStateVersion })
    .then((response) => response.data);

export const retrievePhaseTeamOptions = (query: string, signal?: AbortSignal) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<{ id: string; label: string }[]>
    >("matters/v2/phase-team-options", { params: { q: query || undefined }, signal })
    .then((response) => response.data);

export const retrievePhaseRoleOptions = (query: string, signal?: AbortSignal) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<{ id: string; label: string }[]>
    >("matters/v2/phase-role-options", { params: { q: query || undefined }, signal })
    .then((response) => response.data);

export const retrievePhaseTeamMembers = (
  teamId: string,
  signal?: AbortSignal,
) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<{ userId: string; label: string; roleLabel: string }[]>
    >(`matters/v2/phase-team-options/${teamId}/members`, { signal })
    .then((response) => response.data);
